import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { usePanel } from '../context/PanelContext';
import './OverviewPage.css';

const PARTYKIT_HOST = process.env.REACT_APP_PARTYKIT_HOST || 'thetopia-live.lafless.partykit.dev';
const PROTOCOL = PARTYKIT_HOST.includes('localhost') ? 'http' : 'https';

const ADDON_PARTY_PATH = {
  'command-voting': (slug) => `/party/${slug}`,
  'chunk-delete': (slug) => `/parties/chunk/${slug}`,
  'block-delete': (slug) => `/parties/block/${slug}`,
};

export default function OverviewPage() {
  const { events, loading } = usePanel();
  const [partyStatus, setPartyStatus] = useState({});

  const liveEvents = events.filter((e) => e.webAddon && e.slug && e.visibility);

  const pollPartyKit = useCallback(async () => {
    const withAddon = liveEvents.filter((e) => ADDON_PARTY_PATH[e.webAddon]);
    if (withAddon.length === 0) return;

    const results = {};
    await Promise.all(
      withAddon.map(async (event) => {
        const pathFn = ADDON_PARTY_PATH[event.webAddon];
        try {
          const url = `${PROTOCOL}://${PARTYKIT_HOST}${pathFn(event.slug)}`;
          const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
          if (res.ok) {
            results[event.slug] = await res.json();
          }
        } catch {}
      })
    );
    setPartyStatus(results);
  }, [liveEvents]);

  useEffect(() => {
    if (liveEvents.length === 0) return;
    pollPartyKit();
    const interval = setInterval(pollPartyKit, 15000);
    return () => clearInterval(interval);
  }, [liveEvents.length, pollPartyKit]);

  const now = Date.now();
  const upcomingEvents = events.filter((e) => e.time && new Date(e.time).getTime() > now);
  const totalConnections = Object.values(partyStatus).reduce((sum, s) => sum + (s.connections || 0), 0);
  const activeRooms = liveEvents.filter((e) => partyStatus[e.slug]).length;

  return (
    <div className="ov-page">
      <div className="ov-container">
        <h2 className="ov-title">Overview</h2>

        <div className="ov-stats">
          <div className="ov-stat-card">
            <span className="ov-stat-value">{events.length}</span>
            <span className="ov-stat-label">Total Events</span>
          </div>
          <div className="ov-stat-card">
            <span className="ov-stat-value">{liveEvents.length}</span>
            <span className="ov-stat-label">With Web Addon</span>
          </div>
          <div className="ov-stat-card">
            <span className="ov-stat-value">{totalConnections}</span>
            <span className="ov-stat-label">Live Viewers</span>
          </div>
          <div className="ov-stat-card">
            <span className="ov-stat-value">{activeRooms}</span>
            <span className="ov-stat-label">Active Rooms</span>
          </div>
        </div>

        {upcomingEvents.length > 0 && (
          <section className="ov-section">
            <h3 className="ov-section-title">Upcoming Events</h3>
            <div className="ov-event-list">
              {upcomingEvents.map((event) => (
                <div className="ov-event-row" key={event._id || event.slug}>
                  <span className="ov-event-name">
                    {event.name?.replace(/[§&][0-9a-fk-or]/gi, '').replace(/<#[0-9a-fA-F]{6}>/g, '') || 'Unnamed'}
                  </span>
                  <span className="ov-event-meta">
                    {new Date(event.time).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {liveEvents.length > 0 && (
          <section className="ov-section">
            <h3 className="ov-section-title">PartyKit Rooms</h3>
            <div className="ov-event-list">
              {liveEvents.map((event) => {
                const status = partyStatus[event.slug];
                return (
                  <div className="ov-event-row" key={event.slug}>
                    <div className="ov-pk-info">
                      <span className="ov-event-name">
                        {event.name?.replace(/[§&][0-9a-fk-or]/gi, '').replace(/<#[0-9a-fA-F]{6}>/g, '') || event.slug}
                      </span>
                      <span className="ov-pk-addon">{event.webAddon}</span>
                    </div>
                    {status ? (
                      <span className="ov-pk-status">
                        <span className={`ov-pk-dot ${status.roundState === 'active' ? 'ov-pk-dot-active' : ''}`} />
                        {status.connections ?? 0} viewers
                      </span>
                    ) : (
                      <span className="ov-pk-status ov-pk-status-idle">No data</span>
                    )}
                  </div>
                );
              })}
            </div>
            <Link to="/panel/partykit" className="ov-link">View full PartyKit health &rarr;</Link>
          </section>
        )}

        {loading && <div className="ov-loading">Loading...</div>}
      </div>
    </div>
  );
}
